import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * BoardDemoDrawer
 * Cajón lateral para directivos y evaluadores que aísla los generadores de tráfico
 * simulado (HU-06), evitando que botones de prueba se mezclen con el tablero de mando real.
 */
export const BoardDemoDrawer = ({
    isOpen,
    onClose,
    simulating,
    loadingMetrics,
    onSimulateAccessBurst,
    onSimulatePosBurst,
    onSimulateActivationBurst,
    onRefreshMetrics,
    onResetDemoBoard
}) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 1060,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: '100%',
                        maxWidth: '520px',
                        height: '100%',
                        backgroundColor: '#121216',
                        borderLeft: '2px solid #f59e0b',
                        boxShadow: '-10px 0 35px rgba(0, 0, 0, 0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header del Drawer */}
                    <div
                        style={{
                            padding: '18px 20px',
                            background: 'linear-gradient(180deg, #1c1608 0%, #121216 100%)',
                            borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: '#f59e0b',
                                    color: '#09090b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    fontWeight: 900
                                }}
                            >
                                🛠️
                            </div>
                            <div>
                                <h3
                                    style={{
                                        margin: 0,
                                        fontSize: '1.1rem',
                                        fontWeight: 900,
                                        color: '#ffffff'
                                    }}
                                >
                                    Simulador de Tráfico en Vivo
                                </h3>
                                <span style={{ fontSize: '0.74rem', color: '#fde68a' }}>
                                    Inyección de eventos concurrentes para demostraciones (HU-06)
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'rgba(255, 255, 255, 0.08)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: '#ffffff',
                                borderRadius: 10,
                                width: 34,
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: 16,
                                fontWeight: 700
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Contenido de Acciones de Simulación */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14
                        }}
                    >
                        <div style={{ fontSize: '0.76rem', color: '#a1a1aa', lineHeight: 1.4 }}>
                            Estas acciones disparan eventos de telemetría a través del Hub de SignalR,
                            actualizando métricas de aforo, transacciones y podio en tiempo real.
                        </div>

                        {/* Ráfaga 1: Accesos */}
                        <button
                            type="button"
                            disabled={simulating}
                            onClick={onSimulateAccessBurst}
                            style={{
                                background: 'rgba(16, 185, 129, 0.16)',
                                border: '1.5px solid #10b981',
                                color: '#ffffff',
                                borderRadius: 12,
                                padding: '13px 15px',
                                textAlign: 'left',
                                cursor: simulating ? 'wait' : 'pointer'
                            }}
                        >
                            <div style={{ fontSize: '0.9rem', fontWeight: 900, color: '#6ee7b7' }}>
                                🚪 Simular Ráfaga de Accesos (+5 Asistentes)
                            </div>
                            <div style={{ fontSize: '0.74rem', color: '#d4d4d8', marginTop: 4 }}>
                                Inyecta 5 check-ins en puertas con categorías aleatorias, actualizando los semáforos de aforo.
                            </div>
                        </button>

                        {/* Ráfaga 2: Ventas POS */}
                        <button
                            type="button"
                            disabled={simulating}
                            onClick={onSimulatePosBurst}
                            style={{
                                background: 'rgba(245, 158, 11, 0.16)',
                                border: '1.5px solid #f59e0b',
                                color: '#ffffff',
                                borderRadius: 12,
                                padding: '13px 15px',
                                textAlign: 'left',
                                cursor: simulating ? 'wait' : 'pointer'
                            }}
                        >
                            <div style={{ fontSize: '0.9rem', fontWeight: 900, color: '#fde68a' }}>
                                🍔 Simular Ventas POS (+$2,500 MXN en Stands)
                            </div>
                            <div style={{ fontSize: '0.74rem', color: '#d4d4d8', marginTop: 4 }}>
                                Genera 5 compras distribuidas entre XoloShop y Tacos & Grill, recalculando el ticket promedio.
                            </div>
                        </button>

                        {/* Ráfaga 3: Activaciones */}
                        <button
                            type="button"
                            disabled={simulating}
                            onClick={onSimulateActivationBurst}
                            style={{
                                background: 'rgba(220, 38, 38, 0.18)',
                                border: '1.5px solid #dc2626',
                                color: '#ffffff',
                                borderRadius: 12,
                                padding: '13px 15px',
                                textAlign: 'left',
                                cursor: simulating ? 'wait' : 'pointer'
                            }}
                        >
                            <div style={{ fontSize: '0.9rem', fontWeight: 900, color: '#fca5a5' }}>
                                🎯 Simular Activaciones (+3 Retos de Patrocinadores)
                            </div>
                            <div style={{ fontSize: '0.74rem', color: '#d4d4d8', marginTop: 4 }}>
                                Registra goles anotados en Caliente.mx y pruebas Nissan, alterando el podio de engagement.
                            </div>
                        </button>

                        {/* Controles de Mantenimiento */}
                        <div
                            style={{
                                marginTop: 'auto',
                                paddingTop: 16,
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 10
                            }}
                        >
                            <button
                                type="button"
                                disabled={loadingMetrics || simulating}
                                onClick={onRefreshMetrics}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#ffffff',
                                    borderRadius: 10,
                                    padding: '10px 14px',
                                    fontSize: '0.82rem',
                                    fontWeight: 800,
                                    cursor: 'pointer'
                                }}
                            >
                                🔄 Refrescar Tablero Manualmente
                            </button>

                            <button
                                type="button"
                                disabled={simulating}
                                onClick={onResetDemoBoard}
                                style={{
                                    background: 'rgba(220, 38, 38, 0.2)',
                                    border: '1.5px solid rgba(220, 38, 38, 0.5)',
                                    color: '#fecaca',
                                    borderRadius: 10,
                                    padding: '10px 14px',
                                    fontSize: '0.82rem',
                                    fontWeight: 900,
                                    cursor: 'pointer'
                                }}
                            >
                                🧹 Resetear Tráfico Demo del Tablero
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default BoardDemoDrawer;
